/* Phase 36 — Drink Intelligence wizard (cocktails).
 *
 * Mirror of PlatingWizard with cocktail-specific terminology:
 *   • Step 1 — Bar style (instead of cuisine)
 *   • Step 2 — Glassware (instead of plate shape)
 *   • API endpoint /drink (instead of /plating)
 *   • Saved file pattern drink_<cocktail_name>_<date>.jpg
 *   • Credit field drink_credits (instead of plating_credits)
 *   • Suggestion fields: glass_reason / garnish / ice_type / smoke_effect / pro_tip
 *
 * Same data-testid prefix conventions for the QA harness.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Download, Trash2, Martini } from "lucide-react";
import toast from "react-hot-toast";
import api, { formatApiError } from "../api";
import { Button, Card, Badge, Spinner } from "./ui/Primitives";

const ASPECT_ICON = {
  "1:1":  { w: 28, h: 28 },
  "4:5":  { w: 24, h: 30 },
  "9:16": { w: 18, h: 32 },
  "16:9": { w: 32, h: 18 },
};

function StepLabel({ n, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand/15 text-brand text-[11px] font-semibold">{n}</span>
      <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">{title}</span>
    </div>
  );
}

function downloadBlob(b64, filename) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function slugify(s) {
  return (s || "drink").toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export default function DrinkWizard({ cocktail, onSaved, compact = false }) {
  const [meta, setMeta] = useState(null);
  const [bar, setBar] = useState("classic_bar");
  const [glass, setGlass] = useState("tumbler");
  const [aspect, setAspect] = useState("1:1");
  const [imageCount, setImageCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    api.get("/drink/meta")
      .then(({ data }) => { if (mounted.current) setMeta(data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!cocktail?.id) return;
    api.get(`/drink/cocktail/${cocktail.id}`)
      .then(({ data }) => { if (mounted.current) setSaved(data.items || []); })
      .catch(() => {});
  }, [cocktail?.id]);

  const cost = useMemo(() => {
    if (!meta) return 0;
    return (meta.pricing || []).find((p) => p.image_count === imageCount)?.credits || 0;
  }, [meta, imageCount]);

  const available = meta?.credits?.available ?? 0;
  const canAfford = available >= cost;

  const generate = async () => {
    if (!cocktail?.id) { toast.error("Kein Cocktail ausgewählt"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/drink/generate", {
        cocktail_id: cocktail.id,
        bar_style: bar,
        glassware: glass,
        aspect_ratio: aspect,
        image_count: imageCount,
      });
      if (!mounted.current) return;
      setResult(data);
      setSaved((prev) => [data, ...prev]);
      const { data: m } = await api.get("/drink/meta");
      if (mounted.current) setMeta(m);
      if (onSaved) onSaved(data);
      toast.success("Drink Intelligence hat deine Vorschläge erstellt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const downloadOne = (img, idx) => {
    const fname = `drink_${slugify(cocktail?.name)}_${new Date().toISOString().slice(0, 10)}_${idx + 1}.jpg`;
    downloadBlob(img.b64, fname);
  };
  const downloadAll = (item) => (item.images || []).forEach((img, i) => downloadOne(img, i));
  const removeSaved = async (id) => {
    if (!window.confirm("Diese Drink-Vorschläge löschen?")) return;
    try {
      await api.delete(`/drink/${id}`);
      setSaved((prev) => prev.filter((s) => s.id !== id));
      if (result?.id === id) setResult(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!meta) return <div className="p-6"><Spinner /></div>;

  return (
    <div className={compact ? "space-y-5" : "space-y-6"} data-testid="drink-wizard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Martini size={16} className="text-brand" />
          <span className="text-sm font-medium">Drink Intelligence</span>
        </div>
        <Badge tone={available >= 5 ? "ok" : "warn"} data-testid="drink-credit-badge">
          {available} Drink-Credits
        </Badge>
      </div>

      <div>
        <StepLabel n={1} title="Bar-Stil" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(meta.bar_styles || []).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setBar(c.id)}
              data-testid={`drink-bar-${c.id}`}
              className={`px-3 py-2.5 rounded-lg border text-sm text-left transition ${
                bar === c.id ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
              }`}
            >{c.label}</button>
          ))}
        </div>
      </div>

      <div>
        <StepLabel n={2} title="Glas" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(meta.glassware || []).map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setGlass(g.id)}
              data-testid={`drink-glass-${g.id}`}
              className={`px-3 py-3 rounded-lg border text-sm flex flex-col items-center gap-1.5 transition ${
                glass === g.id ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
              }`}
            >
              <GlassIcon shape={g.id} active={glass === g.id} />
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <StepLabel n={3} title="Bildformat" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(meta.aspect_ratios || []).map((a) => {
            const ic = ASPECT_ICON[a.id] || { w: 24, h: 24 };
            const active = aspect === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => setAspect(a.id)}
                data-testid={`drink-aspect-${a.id.replace(":", "x")}`}
                className={`px-3 py-3 rounded-lg border text-sm flex flex-col items-center gap-1.5 transition ${
                  active ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div
                  className={`rounded-sm border ${active ? "border-brand bg-brand/30" : "border-ink-muted/50 bg-white/5"}`}
                  style={{ width: ic.w, height: ic.h }}
                />
                <span>{a.id} · {a.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <StepLabel n={4} title="Anzahl Bilder" />
        <div className="grid grid-cols-2 gap-2">
          {(meta.pricing || []).map((p) => (
            <button
              key={p.image_count}
              type="button"
              onClick={() => setImageCount(p.image_count)}
              data-testid={`drink-count-${p.image_count}`}
              className={`px-4 py-3 rounded-lg border text-left transition ${
                imageCount === p.image_count ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
              }`}
            >
              <div className="text-sm font-medium">{p.image_count} Bild{p.image_count > 1 ? "er" : ""} + Text</div>
              <div className="text-xs text-ink-muted">{p.credits} Credits</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <div className="text-sm text-ink-muted" data-testid="drink-total-cost">
          Gesamtkosten: <span className="text-ink font-medium">{cost} Credits</span> · verfügbar {available}
        </div>
        <Button
          onClick={generate}
          disabled={loading || !canAfford || !cocktail?.id}
          data-testid="drink-generate-btn"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} />
              Drink Intelligence arbeitet…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Martini size={14} />
              {cost} Credits · Vorschläge generieren
            </span>
          )}
        </Button>
      </div>
      {!canAfford && !loading && (
        <div className="text-xs text-warn">
          Nicht genug Drink-Credits. Bitte Plan upgraden oder Add-on buchen.
        </div>
      )}

      {result && (
        <Card className="p-4 space-y-4 border-brand/40" data-testid="drink-result">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Aktuelle Vorschläge</div>
            <Button onClick={() => downloadAll(result)} variant="ghost" data-testid="drink-download-all">
              <Download size={14} /> Alle herunterladen
            </Button>
          </div>
          <ImageGallery item={result} onDownload={downloadOne} />
          <SuggestionList suggestions={result.suggestions} />
        </Card>
      )}

      {saved.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">Gespeicherte Vorschläge</div>
          {saved.map((s) => (
            <Card key={s.id} className="p-3 space-y-2" data-testid={`drink-history-${s.id}`}>
              <div className="flex items-center justify-between text-xs text-ink-muted gap-2 flex-wrap">
                <span>{new Date(s.created_at).toLocaleString("de-AT")} · {s.image_count} Bilder · {s.aspect_ratio}</span>
                <div className="flex gap-2">
                  <Button onClick={() => downloadAll(s)} variant="ghost" size="sm">
                    <Download size={12} /> Download
                  </Button>
                  <Button onClick={() => removeSaved(s.id)} variant="ghost" size="sm" data-testid={`drink-delete-${s.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
              <ImageGallery item={s} compact onDownload={downloadOne} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageGallery({ item, onDownload, compact = false }) {
  const imgs = item?.images || [];
  if (!imgs.length) return null;
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-3" : "grid-cols-1 md:grid-cols-3"}`}>
      {imgs.map((img, i) => (
        <div key={img.id || i} className="relative group rounded-lg overflow-hidden border border-white/10 bg-white/5">
          <img
            src={`data:${img.mime || "image/png"};base64,${img.b64}`}
            alt={`Drink ${i + 1}`}
            className="w-full h-auto block"
            data-testid={`drink-image-${i}`}
          />
          <button
            type="button"
            onClick={() => onDownload(img, i)}
            data-testid={`drink-download-${i}`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-bg/80 backdrop-blur border border-white/10 opacity-0 group-hover:opacity-100 transition"
          >
            <Download size={11} /> JPG
          </button>
        </div>
      ))}
    </div>
  );
}

function SuggestionList({ suggestions = [] }) {
  if (!suggestions.length) return null;
  return (
    <div className="space-y-3">
      {suggestions.map((s, i) => (
        <div key={i} className="rounded-lg border border-white/10 p-3" data-testid={`drink-suggestion-${i}`}>
          <div className="text-sm font-semibold mb-1">Vorschlag {i + 1}</div>
          <ul className="text-sm space-y-1">
            <li><span className="text-ink-muted">Glas-Begründung: </span>{s.glass_reason}</li>
            <li><span className="text-ink-muted">Garnitur: </span>{s.garnish}</li>
            <li><span className="text-ink-muted">Eis-Typ: </span>{s.ice_type}</li>
            {s.smoke_effect && <li><span className="text-ink-muted">Rauch/Dampf: </span>{s.smoke_effect}</li>}
            <li><span className="text-ink-muted">Profi-Tipp: </span>{s.pro_tip}</li>
          </ul>
        </div>
      ))}
    </div>
  );
}

function GlassIcon({ shape, active }) {
  const cls = active ? "stroke-brand" : "stroke-ink-muted";
  if (shape === "tumbler") return (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none"><path d="M5 4 L15 4 L14 19 L6 19 Z" className={cls} strokeWidth="1.5" /></svg>
  );
  if (shape === "coupe") return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 5 Q11 14 19 5" className={cls} strokeWidth="1.5" /><line x1="11" y1="13" x2="11" y2="20" className={cls} strokeWidth="1.5" /><line x1="6" y1="20" x2="16" y2="20" className={cls} strokeWidth="1.5" /></svg>
  );
  if (shape === "highball") return (
    <svg width="14" height="22" viewBox="0 0 14 22" fill="none"><rect x="3" y="3" width="8" height="17" rx="1" className={cls} strokeWidth="1.5" /></svg>
  );
  return ( // martini
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 4 L19 4 L11 13 Z" className={cls} strokeWidth="1.5" /><line x1="11" y1="13" x2="11" y2="20" className={cls} strokeWidth="1.5" /><line x1="6" y1="20" x2="16" y2="20" className={cls} strokeWidth="1.5" /></svg>
  );
}
